<script context="module" lang="ts">
  export type ButtonStyle = "primary" | "outline-primary" | "secondary" | "outline-secondary" | "link";
</script>

<script lang="ts">
  import type { StoresKey } from "~/stores";
  import { getStoresContext } from "~/stores";
  import { nanoid } from "nanoid";
  import CardBaseSwiper from "../CardBaseSwiper.svelte";
  import StoreCard from "../store/StoreCard.svelte";
  import OfferCard from "../offer/OfferCard.svelte";
  import EventCard from "../event/EventCard.svelte";
  import type { ManageAPI } from "~/manage-api/types";
  import { Navigation, Pagination } from "swiper/modules";

  // Props from Astro
  export let request_store_key: StoresKey;
  export let contentType: string = "all-stores";
  export let itemLimit: number = 6;
  export let showNavButtons: boolean = true;
  export let showSeeAllButton: boolean = true;
  export let seeAllButtonText: string = "See all";
  export let noContentText: string = "No content currently available";
  
  // Content-related props
  export let currentStoreId: string = "";
  export let customUniqueId: string | undefined = undefined;
  export let searchQuery: string = "";
  export let randomizeItems: boolean = false;
  export let uniquePerStore: boolean = false;
  
  // Button styling props - override site-wide defaults if provided
  export let buttonStyle: ButtonStyle = "primary";
  export let letterSpacing: string = "";
  export let buttonPaddingVertical: string = "";
  export let buttonPaddingHorizontal: string = "";
  
  // Navigation button styling props - these will override site-wide values if provided
  export let navButtonBorderColor: string = "";
  export let navButtonBorderStyle: string = "";
  export let navButtonBorderThickness: string = "";
  export let navButtonBorderRadius: string = "";
  export let navButtonChevronColor: string = "";
  export let navButtonBackgroundColor: string = "";
  export let navButtonWidth: string = "";
  export let navButtonHeight: string = "";
  export let navButtonHoverBorderColor: string = "";
  export let navButtonHoverBackgroundColor: string = "";
  
  // Pagination styling props - these will override site-wide values if provided
  export let paginationBulletWidth: string = "";
  export let paginationBulletHeight: string = "";
  export let paginationActiveBulletWidth: string = "";
  export let paginationActiveBulletHeight: string = "";
  export let paginationBulletBorderRadius: string = "";
  export let paginationInactiveBulletColor: string = "";
  export let paginationActiveBulletColor: string = "";
  
  // Fragment-level style overrides for cards - these override site-wide values if provided
  // Store card styling
  export let storeCardBorderRadius: string = "";
  export let storeCardBorderThickness: string = "";
  export let storeCardBorderColor: string = "";
  export let storeCardBorderStyle: string = "";
  export let storeCardHoverEffect: string = "";
  
  // Offer card styling
  export let offerCardBorderRadius: string = "";
  export let offerCardBorderThickness: string = "";
  export let offerCardBorderColor: string = "";
  export let offerCardBorderStyle: string = "";
  export let offerCardHoverEffect: string = "";
  
  // Event card styling
  export let eventCardBorderRadius: string = "";
  export let eventCardBorderThickness: string = "";
  export let eventCardBorderColor: string = "";
  export let eventCardBorderStyle: string = "";
  export let eventCardHoverEffect: string = "";
  
  // Helper function to handle empty string values from select fields
  function useValueOrDefault(value: string): string {
    // If the value is an empty string (from the "Default" option), return empty string
    // so CSS variables will be used instead
    return value === "" ? "" : value;
  }

  // Local state variables
  const generatedUniqueId = `content-swiper-${nanoid(6)}`;
  $: uniqueId = customUniqueId || generatedUniqueId;
  
  // Get stores context
  const { store, stores, offers, events, reverse_router } = getStoresContext(request_store_key);

  // Use CSS variables with prop overrides
  $: actualButtonStyle = buttonStyle;
  $: actualLetterSpacing = letterSpacing || "var(--see-all-button-letter-spacing, 1.5px)";
  $: actualButtonPaddingVertical = buttonPaddingVertical || "var(--see-all-button-padding-vertical, 12px)";
  $: actualButtonPaddingHorizontal = buttonPaddingHorizontal || "var(--see-all-button-padding-horizontal, 18px)";

  // Process navigation button styling
  $: actualNavButtonBorderColor = navButtonBorderColor || "";
  $: actualNavButtonBorderStyle = useValueOrDefault(navButtonBorderStyle) || "";
  $: actualNavButtonBorderThickness = navButtonBorderThickness || "";
  $: actualNavButtonBorderRadius = navButtonBorderRadius || "";
  $: actualNavButtonChevronColor = navButtonChevronColor || "";
  $: actualNavButtonBackgroundColor = navButtonBackgroundColor || "";
  $: actualNavButtonWidth = navButtonWidth || "";
  $: actualNavButtonHeight = navButtonHeight || "";
  $: actualNavButtonHoverBorderColor = navButtonHoverBorderColor || "";
  $: actualNavButtonHoverBackgroundColor = navButtonHoverBackgroundColor || "";
  
  // Process pagination styling
  $: actualPaginationBulletWidth = paginationBulletWidth || "";
  $: actualPaginationBulletHeight = paginationBulletHeight || "";
  $: actualPaginationActiveBulletWidth = paginationActiveBulletWidth || "";
  $: actualPaginationActiveBulletHeight = paginationActiveBulletHeight || "";
  $: actualPaginationBulletBorderRadius = paginationBulletBorderRadius || "";
  $: actualPaginationInactiveBulletColor = paginationInactiveBulletColor || "";
  $: actualPaginationActiveBulletColor = paginationActiveBulletColor || "";

  // Process store card styling props
  $: actualStoreCardBorderStyle = useValueOrDefault(storeCardBorderStyle) || "";
  $: actualStoreCardHoverEffect = useValueOrDefault(storeCardHoverEffect) || "";

  // Process offer card styling props 
  $: actualOfferCardBorderStyle = useValueOrDefault(offerCardBorderStyle) || "";
  $: actualOfferCardHoverEffect = useValueOrDefault(offerCardHoverEffect) || "";
  
  // Process event card styling props
  $: actualEventCardBorderStyle = useValueOrDefault(eventCardBorderStyle) || "";
  $: actualEventCardHoverEffect = useValueOrDefault(eventCardHoverEffect) || "";

  // Swiper configuration
  $: swiperOptions = {
    modules: [Navigation, Pagination],
    slidesPerView: 'auto' as const,
    spaceBetween: 24,
    pagination: {
      enabled: true,
      clickable: true,
      dynamicBullets: true,
      dynamicMainBullets: 3
    },
    watchOverflow: true,
    observer: true,
    observeParents: true,
    resizeObserver: true,
    updateOnWindowResize: true
  };

  // Make sure we always have a text when showSeeAllButton is true
  $: actualSeeAllButtonText = showSeeAllButton && seeAllButtonText ? seeAllButtonText : "See all";

  // Track if we have a valid URL
  $: hasValidUrl = !!seeAllUrl && seeAllUrl !== "#";

  // Generate URL based on content type
  $: seeAllUrl = generateSeeAllUrl(contentType, $reverse_router);

  function generateSeeAllUrl(type: string, router: any): string {
    if (!router || typeof router.routePurpose !== 'function') {
      return "#";
    }
    
    try {
      let url = "#";
      switch (type) {
        case "all-stores":
        case "related-stores":
        case "search-stores":
          url = router.routePurpose("stores collection");
          break;
        case "all-offers":
        case "related-offers":
        case "search-offers":
          url = router.routePurpose("offers collection");
          break;
        case "all-events":
        case "search-events":
          url = router.routePurpose("events collection");
          break;
      }
      
      // Validate the URL
      if (!url || url === "" || url === "#") {
        console.warn(`Invalid URL generated for content type ${type}`);
        return "#";
      }
      
      return url;
    } catch (e) {
      console.error("Error generating URL:", e);
      return "#";
    }
  }

  // Add type guards for Store, Offer, and Event
  function isStore(item: any): item is ManageAPI.Store {
    return item && typeof item === 'object' && 'name' in item;
  }
  function isOffer(item: any): item is ManageAPI.Offer {
    return item && typeof item === 'object' && 'title' in item && 'endDate' in item;
  }
  function isEvent(item: any): item is ManageAPI.Event {
    return item && typeof item === 'object' && 'content' in item && 'introduction' in item;
  }

  // Helper function to shuffle an array (for randomization)
  function shuffleArray<T>(array: T[]): T[] {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  }

  // Helper function to ensure only one item per store (for offers)
  function filterUniquePerStore<T extends { store?: { id: number | string } }>(items: T[]): T[] {
    const seen = new Set<string>();
    return items.filter(item => {
      if (!item.store?.id) return true;
      const storeId = item.store.id.toString();
      if (seen.has(storeId)) return false;
      seen.add(storeId);
      return true;
    });
  }

  // Helper function to ensure unique events (by id)
  function filterUniqueEvents(events: ManageAPI.Event[]): ManageAPI.Event[] {
    const seen = new Set<string>();
    return events.filter(event => {
      if (!event.id) return true;
      const eventId = event.id.toString();
      if (seen.has(eventId)) return false;
      seen.add(eventId);
      return true;
    });
  }

  // Helper function to sort items alphabetically
  function sortAlphabetically<T extends { name?: string, title?: string }>(items: T[]): T[] {
    return [...items].sort((a, b) => {
      const nameA = (a.name || a.title || '').toLowerCase();
      const nameB = (b.name || b.title || '').toLowerCase();
      return nameA.localeCompare(nameB);
    });
  }

  $: filteredItems = (() => {
    // Create properly typed arrays based on content type
    if (contentType === "all-stores" || contentType === "related-stores" || contentType === "search-stores") {
      let storeItems: ManageAPI.Store[] = [];
      
      // Get base items
      switch (contentType) {
        case "all-stores":
          storeItems = [...$stores];
          break;
        case "related-stores":
          if (!currentStoreId) return [];
          
          // Find the current store to get its FOB
          const currentStore = $stores.find(s => s.id.toString() === currentStoreId);
          if (!currentStore) return [];
          
          // Get the current store's FOB IDs (primary and additional)
          const currentStoreFobIds = [
            currentStore.fieldOfBusinessId,
            ...(currentStore.additionalFieldOfBusinessIds || [])
          ];
          
          // Filter stores that share at least one FOB with the current store, but exclude the current store
          storeItems = [...$stores].filter(s => {
            if (s.id.toString() === currentStoreId) return false;
            const storeFobIds = [
              s.fieldOfBusinessId,
              ...(s.additionalFieldOfBusinessIds || [])
            ];
            
            // Check if any of the store's FOB IDs match any of the current store's FOB IDs
            return currentStoreFobIds.some(fobId => storeFobIds.includes(fobId));
          });
          break;
        case "search-stores":
          if (!searchQuery) return [];
          storeItems = [...$stores].filter(store => 
            store?.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            store?.description?.toLowerCase()?.includes(searchQuery.toLowerCase())
          );
          break;
      }
      
      // Sort search results alphabetically
      if (contentType === "search-stores") {
        storeItems = sortAlphabetically(storeItems);
      }
      // Randomize if needed
      else if (randomizeItems) {
        storeItems = shuffleArray(storeItems);
      }
      
      // Apply item limit (if itemLimit is > 0)
      // When itemLimit is 0, show all items (no limit)
      if (itemLimit > 0) {
        storeItems = storeItems.slice(0, itemLimit);
      }
      
      return storeItems;
    }
    
    else if (contentType === "all-offers" || contentType === "related-offers" || contentType === "search-offers") {
      let offerItems: ManageAPI.Offer[] = [];
      
      // Get base items
      switch (contentType) {
        case "all-offers":
          offerItems = [...$offers];
          break;
        case "related-offers":
          if (!currentStoreId) return [];
          offerItems = [...$offers].filter(o => o.store?.id.toString() === currentStoreId);
          break;
        case "search-offers":
          if (!searchQuery) return [];
          offerItems = [...$offers].filter(offer => 
            offer?.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            offer?.description1?.toLowerCase()?.includes(searchQuery.toLowerCase()) ||
            offer?.description2?.toLowerCase()?.includes(searchQuery.toLowerCase()) ||
            offer?.store?.name?.toLowerCase().includes(searchQuery.toLowerCase())
          );
          break;
      }
      
      // Apply unique per store filter if needed
      if (uniquePerStore) {
        offerItems = filterUniquePerStore(offerItems);
      }
      
      // Sort search results alphabetically
      if (contentType === "search-offers") {
        offerItems = sortAlphabetically(offerItems);
      }
      // Randomize if needed
      else if (randomizeItems) {
        offerItems = shuffleArray(offerItems);
      }
      
      // Apply item limit (if itemLimit is > 0)
      // When itemLimit is 0, show all items (no limit)
      if (itemLimit > 0) {
        offerItems = offerItems.slice(0, itemLimit);
      }
      
      return offerItems;
    }
    
    else if (contentType === "all-events" || contentType === "search-events") {
      let eventItems: ManageAPI.Event[] = [];
      
      // Get base items
      switch (contentType) {
        case "all-events":
          eventItems = [...$events];
          break;
        case "search-events":
          if (!searchQuery) return [];
          eventItems = [...$events].filter(event => 
            event?.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            event?.keywords?.some(keyword => keyword.toLowerCase().includes(searchQuery.toLowerCase()))
          );
          break;
      }
      
      // Apply unique per store filter if needed (for events section)
      if (uniquePerStore) {
        // For events, we may not have store info, so we'll just ensure unique event IDs
        eventItems = filterUniqueEvents(eventItems);
      }
      
      // Sort search results alphabetically
      if (contentType === "search-events") {
        eventItems = sortAlphabetically(eventItems);
      }
      // Randomize if needed
      else if (randomizeItems) {
        eventItems = shuffleArray(eventItems);
      }
      
      // Apply item limit (if itemLimit is > 0)
      // When itemLimit is 0, show all items (no limit)
      if (itemLimit > 0) {
        eventItems = eventItems.slice(0, itemLimit);
      }
      
      return eventItems;
    }
    
    return [];
  })();
</script>

{#if filteredItems.length > 0}
  <div class="swiper-container">
    <CardBaseSwiper 
      {uniqueId} 
      {showNavButtons}
      showSeeAllButton={showSeeAllButton && hasValidUrl}
      seeAllButtonText={actualSeeAllButtonText}
      seeAllUrl={seeAllUrl}
      buttonStyle={actualButtonStyle}
      letterSpacing={actualLetterSpacing}
      buttonPaddingVertical={actualButtonPaddingVertical}
      buttonPaddingHorizontal={actualButtonPaddingHorizontal}
      navButtonBorderColor={actualNavButtonBorderColor}
      navButtonBorderStyle={actualNavButtonBorderStyle}
      navButtonBorderThickness={actualNavButtonBorderThickness}
      navButtonBorderRadius={actualNavButtonBorderRadius}
      navButtonChevronColor={actualNavButtonChevronColor}
      navButtonBackgroundColor={actualNavButtonBackgroundColor}
      navButtonWidth={actualNavButtonWidth}
      navButtonHeight={actualNavButtonHeight}
      navButtonHoverBorderColor={actualNavButtonHoverBorderColor}
      navButtonHoverBackgroundColor={actualNavButtonHoverBackgroundColor}
      paginationBulletWidth={actualPaginationBulletWidth}
      paginationBulletHeight={actualPaginationBulletHeight}
      paginationActiveBulletWidth={actualPaginationActiveBulletWidth}
      paginationActiveBulletHeight={actualPaginationActiveBulletHeight}
      paginationBulletBorderRadius={actualPaginationBulletBorderRadius}
      paginationInactiveBulletColor={actualPaginationInactiveBulletColor}
      paginationActiveBulletColor={actualPaginationActiveBulletColor}
      {swiperOptions}
    >
      {#if contentType === "all-stores" || contentType === "related-stores" || contentType === "search-stores"}
        {#each filteredItems as item}
          {#if isStore(item)}
            <div class="swiper-slide">
              <div class="card-wrapper">
                <StoreCard 
                  store={item} 
                  {request_store_key}
                  _class="" 
                  borderRadius={storeCardBorderRadius}
                  borderThickness={storeCardBorderThickness}
                  borderColor={storeCardBorderColor}
                  borderStyle={actualStoreCardBorderStyle}
                  hoverEffect={actualStoreCardHoverEffect}
                />
              </div>
            </div>
          {/if}
        {/each}
      {:else if contentType === "all-offers" || contentType === "related-offers" || contentType === "search-offers"}
        {#each filteredItems as item}
          {#if isOffer(item)}
            <div class="swiper-slide">
              <div class="card-wrapper">
                <OfferCard 
                  offer={item} 
                  {request_store_key}
                  _class=""
                  borderRadius={offerCardBorderRadius}
                  borderThickness={offerCardBorderThickness}
                  borderColor={offerCardBorderColor}
                  borderStyle={actualOfferCardBorderStyle}
                  hoverEffect={actualOfferCardHoverEffect}
                />
              </div>
            </div>
          {/if}
        {/each}
      {:else if contentType === "all-events" || contentType === "search-events"}
        {#each filteredItems as item}
          {#if isEvent(item)}
            <div class="swiper-slide">
              <div class="card-wrapper">
                <EventCard 
                  event={item} 
                  {request_store_key}
                  _class=""
                  borderRadius={eventCardBorderRadius}
                  borderThickness={eventCardBorderThickness}
                  borderColor={eventCardBorderColor}
                  borderStyle={actualEventCardBorderStyle}
                  hoverEffect={actualEventCardHoverEffect}
                />
              </div>
            </div>
          {/if}
        {/each}
      {/if}
    </CardBaseSwiper>
  </div>
{:else}
  <div class="empty-container">
    <p>{noContentText}</p>
  </div>
{/if}

<style lang="scss">
  .swiper-container {
    width: 100%;
  }

  .card-wrapper {
    height: 100%;
    overflow: visible;
    padding-top: 5px;
  }

  .empty-container {
    width: 100%;
    padding: 3rem 0;
    text-align: center;
    
    p {
      font-family: var(--liferay-font-family-sans-serif);
      font-size: 1rem;
      color: #555;
    }
  }
</style> 